import { useState } from 'react';
import { clsx } from 'clsx';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Check, Minus } from 'lucide-react';

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  className?: string;
  render: (row: T, index: number) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  pageSize?: number;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  className?: string;
  /** Enable row selection with checkboxes */
  selectable?: boolean;
  /** Set of selected row IDs (requires selectable) */
  selectedIds?: Set<string>;
  /** Toggle selection of a single row by ID */
  onToggle?: (id: string) => void;
  /** Toggle all / deselect all */
  onToggleAll?: () => void;
  /** Whether all items are currently selected */
  isAllSelected?: boolean;
}

function RowCheckbox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      className={clsx(
        'flex items-center justify-center h-4 w-4 rounded border transition-colors',
        checked
          ? 'bg-nexus-600 border-nexus-600 text-white'
          : 'border-surface-500 hover:border-surface-400 text-transparent',
      )}
    >
      <Check className="h-3 w-3" strokeWidth={3} />
    </button>
  );
}

function HeaderCheckbox({ checked, indeterminate, onChange }: { checked: boolean; indeterminate: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={clsx(
        'flex items-center justify-center h-4 w-4 rounded border transition-colors',
        checked || indeterminate
          ? 'bg-nexus-600 border-nexus-600 text-white'
          : 'border-surface-500 hover:border-surface-400 text-transparent',
      )}
    >
      {indeterminate ? (
        <Minus className="h-3 w-3" strokeWidth={3} />
      ) : (
        <Check className="h-3 w-3" strokeWidth={3} />
      )}
    </button>
  );
}

export function DataTable<T extends { id?: string }>({
  columns,
  data,
  pageSize = 10,
  onRowClick,
  emptyMessage = 'No data found',
  className,
  selectable = false,
  selectedIds,
  onToggle,
  onToggleAll,
  isAllSelected = false,
}: DataTableProps<T>) {
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Sort data
  const sorted = sortKey
    ? [...data].sort((a, b) => {
        const aVal = (a as Record<string, unknown>)[sortKey];
        const bVal = (b as Record<string, unknown>)[sortKey];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        }
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        return sortDir === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      })
    : data;

  const totalPages = Math.ceil(sorted.length / pageSize);
  const start = page * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const colCount = selectable ? columns.length + 1 : columns.length;
  const hasSelection = selectable && selectedIds && selectedIds.size > 0;

  return (
    <div className={clsx('overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="border-b border-surface-700">
              {selectable && (
                <th className="px-4 py-3 w-10">
                  <HeaderCheckbox
                    checked={isAllSelected}
                    indeterminate={!!hasSelection && !isAllSelected}
                    onChange={() => onToggleAll?.()}
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={clsx(
                    'px-4 py-3 text-left text-xs font-semibold text-surface-400 uppercase tracking-wider',
                    col.sortable && 'cursor-pointer select-none hover:text-surface-200',
                    col.className,
                  )}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    {col.header}
                    {col.sortable && sortKey === col.key && (
                      sortDir === 'asc' ? (
                        <ChevronUp className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-700/50">
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="px-4 py-12 text-center text-surface-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              pageData.map((row, idx) => {
                const rowId = (row as { id?: string }).id;
                const isSelected = selectable && rowId ? selectedIds?.has(rowId) ?? false : false;
                return (
                <tr
                  key={start + idx}
                  className={clsx(
                    'transition-colors hover:bg-surface-700/30',
                    onRowClick && 'cursor-pointer',
                    isSelected && 'bg-nexus-600/5',
                  )}
                  onClick={() => onRowClick?.(row)}
                >
                  {selectable && rowId && (
                    <td className="px-4 py-3 w-10">
                      <RowCheckbox
                        checked={isSelected}
                        onChange={() => onToggle?.(rowId)}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={clsx('px-4 py-3 text-sm', col.className)}>
                      {col.render(row, start + idx)}
                    </td>
                  ))}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-surface-700">
          <p className="text-xs text-surface-500">
            Showing {start + 1}–{Math.min(start + pageSize, data.length)} of {data.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="p-1 rounded text-surface-400 hover:text-surface-200 hover:bg-surface-700 disabled:opacity-30 disabled:pointer-events-none"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={clsx(
                  'px-2.5 py-1 text-xs rounded font-medium',
                  i === page
                    ? 'bg-nexus-600 text-white'
                    : 'text-surface-400 hover:bg-surface-700 hover:text-surface-200',
                )}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page === totalPages - 1}
              className="p-1 rounded text-surface-400 hover:text-surface-200 hover:bg-surface-700 disabled:opacity-30 disabled:pointer-events-none"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
